'use client';

import { websiteEnv } from '@/constants/website-env';
import { useAppContext } from '@/context/app';
import useIsMobile from '@/hooks/use-is-mobile';
import { useSyncCart } from '@/hooks/use-sync-cart';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import SearchBarLazyLoad from '../SearchBar/search-bar-lazy-load';
import CartTrigger from './cartTrigger';
import HeaderRectCalculate from './header-rect-calculate';
import RecordInfluencer from './record-influencer';

const CountrySwitcher = dynamic(() => import('./country-switcher'), {
  ssr: false,
  loading: () => <div className='h-5 w-[22px] lg_w-[46px]' />,
});

const HeaderToolbar = props => {
  const {
    miniCartOpen,
    setMiniCartOpen,
    setShowNoItemDrawer,
    announcements,
    headerId,
    navTrigger,
    getShouldResetHeaderOnTop,
    setShouldResetHeaderOnTop,
  } = props;

  const isMobile = useIsMobile();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const headerToolbarRef = useRef<HTMLDivElement>(null);
  const mobileHeaderMenuRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const [
    { mobileFilterBarFixedOnTop, activeColumn, cartActiveColumn },
    {
      actions: { setActiveColumn },
    },
  ] = useAppContext();
  const realActiveColumn = pathname === '/cart' ? cartActiveColumn : activeColumn;

  useEffect(() => {
    if (pathname === '/') {
      setActiveColumn('home');
      return;
    }

    if (pathname === '/attire') {
      setActiveColumn('attire');
    }
  }, [pathname, setActiveColumn]);

  useSyncCart();

  return (
    <div
      ref={headerToolbarRef}
      className={clsx(mobileFilterBarFixedOnTop && 'invisible')}
    >
      <Suspense fallback={null}>
        <HeaderRectCalculate
          headerToolbarRef={headerToolbarRef}
          mobileHeaderMenuRef={mobileHeaderMenuRef}
          headerId={headerId}
          getShouldResetHeaderOnTop={getShouldResetHeaderOnTop}
          setShouldResetHeaderOnTop={setShouldResetHeaderOnTop}
        />
        <RecordInfluencer />
      </Suspense>
      {announcements}
      <div className='flex items-center lg_justify-between lg_pl-5 lg_pr-10'>
        <div className='flex flex-1 items-center  px-[3px] lg_flex-none'>
          <Link
            href='/'
            className={clsx(
              'flex h-10 flex-shrink flex-grow-0 basis-[150px] items-end lg_h-[60px] lg_flex-none lg_basis-[170px] ',
            )}
          >
            <div className='flex flex-1'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='35'
                fill='none'
                viewBox='0 0 16 35'
                className={clsx(realActiveColumn !== 'home' && 'invisible', 'flex-none lg_hidden')}
              >
                <path
                  fill='#F8F3EF'
                  d='M0 35c3.896 0 7.07-4.078 7.07-9.123V11.541C7.07 5.168 11.063 0 16 0v35'
                ></path>
              </svg>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='25'
                height='54'
                fill='none'
                viewBox='0 0 25 54'
                className={clsx('hidden flex-none lg_block', realActiveColumn !== 'home' && 'invisible')}
                style={{ transform: 'rotateY(180deg)' }}
              >
                <path
                  fill='#F8F3EF'
                  d='M24.61 54c-5.993 0-10.875-6.292-10.875-14.075V17.807C13.735 7.973 7.593 0 0 0v54'
                ></path>
              </svg>

              <div
                className={clsx(
                  'relative flex flex-1 items-center justify-center px-[5px] lg_px-2.5',
                  realActiveColumn === 'home' &&
                    ' bg-f8f3ef after_absolute after_bottom-0 after_left-0 after_h-[1px] after_w-full after_bg-BE9983 after_bg-clip-content after_px-[5px] after_content-[""] lg_after_px-2.5',
                )}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  xmlSpace='preserve'
                  x='0'
                  y='0'
                  version='1.1'
                  className={clsx('h-auto w-full lg_w-[130px]')}
                  viewBox='0 0 130 30'
                >
                  <path
                    d='M8.37 21.81v-.25c.18-.01.33-.02.43-.05q.15-.045.42-.27c.27-.225.35-.39.52-.7s.39-.76.65-1.35c.26-.58.56-1.31.88-2.19l3.23-8.8h.22c.7 1.69 1.45 3.51 2.24 5.48.79 1.96 1.41 3.51 1.85 4.63l.66 1.69c.13.3.25.55.37.76q.18.3.3.45c.12.15.19.18.32.24s.22.09.28.09c.06.01.17.02.32.03v.25h-4.41v-.25c.51 0 .77-.25.77-.77 0-.35-.12-.83-.36-1.42l-.67-1.68h-5.02c-.33.97-.51 1.68-.54 2.12-.01.08-.01.16-.01.24 0 1 .58 1.5 1.73 1.51h.14v.25H8.37zm3.05-4.37h4.88l-2.53-6.42zM21.07 8.44v-.25h5.22v.25q-.345 0-.6.09c-.255.09-.3.13-.39.21s-.16.19-.2.32-.07.24-.08.33-.01.2.01.33.03.21.04.25c.01.03.02.08.04.14l3.33 9.05 2.13-6.32-1-2.88c-.13-.33-.26-.61-.42-.82s-.31-.37-.48-.46-.33-.16-.46-.19c-.14-.03-.31-.04-.5-.04v-.26h5.21v.25q-.345 0-.6.09c-.255.09-.32.14-.41.22s-.15.18-.19.32-.07.24-.08.33-.01.2.02.34.04.22.04.24.02.06.04.14l3.17 9.12 2.18-6.22c.03-.1.08-.25.14-.46.11-.35.19-.62.25-.81s.12-.45.19-.78.11-.6.11-.79-.02-.41-.07-.66-.13-.44-.26-.57-.32-.25-.56-.34c-.24-.1-.53-.14-.88-.14v-.28h4.91v.25c-.31 0-.57.03-.77.09-.21.06-.42.17-.64.34s-.43.43-.64.78-.43.79-.68 1.33-.53 1.23-.85 2.08l-3.17 8.75h-.27l-3.21-8.61-2.92 8.61h-.28L22.92 9.96c-.13-.33-.26-.61-.41-.82s-.31-.37-.48-.46c-.17-.1-.33-.16-.46-.19-.14-.03-.31-.05-.5-.05M49.13 21.81v-.17c.75 0 1.12-.72 1.12-2.15v-9.01c0-1.4-.37-2.11-1.12-2.11v-.18h3.83c.34 0 .63 0 .87.01s.51.02.82.05c.3.03.56.06.77.1s.44.1.68.18c.25.08.45.18.61.29s.32.25.48.42.29.36.38.57.17.46.22.75c.06.28.08.6.08.94 0 .57-.1 1.06-.3 1.5-.2.43-.43.76-.69.98s-.56.4-.88.54c-.33.14-.59.23-.78.26-.2.04-.35.05-.48.05q.315 0 .6.03c.19.02.43.05.71.11s.54.13.77.22.46.23.71.41c.24.18.45.39.62.63s.31.54.41.91c.11.37.16.77.16 1.22 0 .47-.05.89-.16 1.25s-.24.67-.4.91-.39.45-.69.61-.59.3-.86.39-.64.16-1.09.21c-.46.05-.86.08-1.2.08-.34.01-.8.01-1.37.01zm3.12-7.03h1.15c.22 0 .42-.01.6-.04s.37-.08.57-.16.38-.2.52-.35c.15-.16.28-.35.41-.59.12-.24.22-.54.28-.91.07-.37.1-.79.1-1.26 0-.54-.06-1.01-.18-1.4q-.18-.585-.45-.9c-.18-.2-.4-.36-.68-.48-.27-.12-.52-.19-.73-.21s-.46-.04-.75-.04h-.85v6.34zm0 6.78h.85c.9 0 1.53-.05 1.89-.14.76-.2 1.27-.7 1.52-1.48.14-.44.21-1 .21-1.66 0-.34-.02-.65-.07-.93s-.11-.53-.18-.74-.17-.39-.29-.56c-.12-.16-.24-.3-.36-.41s-.27-.2-.44-.28-.33-.14-.47-.18-.31-.07-.51-.09-.37-.03-.5-.04h-1.65zM61.66 21.81v-.25c.43 0 .73-.14.91-.42s.27-.81.27-1.59V9.91c0-.58-.09-.97-.26-1.17s-.48-.3-.92-.3v-.25h3.65q.87 0 1.53.03c.66.03.87.07 1.29.13.43.07.79.16 1.09.27s.58.26.85.45c.26.19.47.41.63.67s.28.57.36.92.13.76.13 1.22c0 .64-.08 1.19-.25 1.64s-.41.81-.74 1.07-.68.46-1.08.58-.86.21-1.4.24c.15.01.27.02.37.03s.22.03.37.05c.15.03.27.06.37.11l.36.18c.14.07.26.17.37.29s.22.25.35.41.24.34.35.56.21.47.33.74c.11.27.22.58.31.93.1.35.2.72.31 1.13.25 1.05.94 1.61 2.08 1.7v.25h-2.2q-.21 0-.54-.06c-.22-.04-.36-.07-.43-.09-.07-.03-.18-.1-.31-.23-.14-.13-.22-.24-.26-.34s-.11-.28-.21-.54-.16-.42-.17-.48-.12-.47-.31-1.24c-.12-.5-.23-.91-.31-1.21-.08-.32-.2-.61-.36-.86s-.33-.45-.53-.59-.39-.26-.57-.35-.38-.15-.6-.19c-.22-.03-.38-.05-.49-.06s-.22-.01-.36-.01h-.88v4.23c0 .64.1 1.09.3 1.36.2.26.5.4.92.4v.25h-4.32zm3.1-6.5h.6c.99 0 1.67-.05 2.05-.14.73-.18 1.23-.62 1.49-1.3.19-.5.29-1.16.29-1.98 0-.48-.04-.9-.11-1.27-.08-.37-.16-.67-.26-.92-.1-.24-.25-.45-.46-.61s-.39-.29-.55-.37-.39-.15-.71-.19-.57-.06-.75-.07-.47-.01-.84-.01h-.74v6.86zM75.68 21.81v-.25c.91 0 1.36-.6 1.36-1.81V9.97c0-.57-.11-.97-.34-1.2s-.57-.34-1.02-.34v-.24h4.71v.25c-.45 0-.78.11-1.01.34s-.34.62-.34 1.19v9.77c0 1.21.45 1.81 1.35 1.81v.25h-4.71zM83.34 21.81v-.25c.42 0 .74-.18.97-.53.23-.36.34-.86.34-1.52v-9.03c0-.62-.11-1.11-.34-1.48s-.55-.55-.97-.55v-.26h4.16c.59 0 1.13.03 1.64.08s1.03.15 1.56.29 1.01.32 1.44.54.84.51 1.22.87.71.77.97 1.24.47 1.03.62 1.68.23 1.37.23 2.16c0 .81-.08 1.55-.23 2.21s-.36 1.22-.61 1.69c-.26.46-.58.87-.97 1.22s-.8.63-1.22.84-.9.38-1.45.5c-.54.13-1.06.21-1.56.25s-1.04.07-1.64.07h-4.16zm3.31-.25h.85c.4 0 .72-.01.97-.02s.58-.06 1-.15.76-.21 1.04-.37q.405-.24.9-.72c.495-.48.59-.71.78-1.16s.35-1.03.49-1.72c.13-.7.2-1.49.2-2.38 0-.86-.06-1.64-.19-2.33s-.29-1.26-.49-1.71-.45-.85-.77-1.18-.61-.58-.9-.75-.63-.31-1.02-.41q-.585-.15-1.02-.18c-.28-.02-.61-.03-.97-.03h-.85v13.11zM96.76 21.81v-.25c.18-.01.33-.02.43-.05q.15-.045.42-.27c.27-.225.35-.39.52-.7s.39-.76.65-1.35c.26-.58.56-1.31.88-2.19l3.23-8.8h.22c.7 1.69 1.45 3.51 2.24 5.48.79 1.96 1.41 3.51 1.85 4.63l.66 1.69c.13.3.25.55.37.76q.18.3.3.45c.12.15.19.18.32.24s.22.09.28.09c.06.01.17.02.32.03v.25h-4.41v-.25c.51 0 .77-.25.77-.77 0-.35-.12-.83-.36-1.42l-.67-1.68h-5.02c-.33.97-.51 1.68-.54 2.12-.01.08-.01.16-.01.24 0 1 .58 1.5 1.73 1.51h.14v.25h-4.32zm3.05-4.37h4.88l-2.53-6.42zM111.81 8.44v-.25h4.65v.25c-.47 0-.84.13-1.09.37-.26.25-.38.7-.38 1.35v11.39h.7c.1.01.21.01.32.01s.23 0 .34-.01c.23-.01.46-.02.7-.02s.48-.03.72-.07.49-.09.72-.15.47-.13.7-.24.44-.23.64-.36.39-.3.57-.5.33-.42.46-.67.24-.53.34-.86c.09-.33.15-.68.18-1.07h.25v4.19h-9.66v-.25c.41 0 .7-.17.86-.51s.25-.85.25-1.54v-9.44c0-.58-.1-.99-.3-1.25-.22-.24-.53-.37-.97-.37'
                    className='fill-[#5e5047]'
                  ></path>
                </svg>
              </div>

              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='35'
                fill='none'
                viewBox='0 0 16 35'
                className={clsx(realActiveColumn !== 'home' && 'invisible', 'flex-none lg_hidden')}
                style={{ transform: 'rotateY(180deg)' }}
              >
                <path
                  fill='#F8F3EF'
                  d='M0 35c3.896 0 7.07-4.078 7.07-9.123V11.541C7.07 5.168 11.063 0 16 0v35'
                ></path>
              </svg>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='25'
                height='54'
                fill='none'
                viewBox='0 0 25 54'
                className={clsx('hidden flex-none lg_block', realActiveColumn !== 'home' && 'invisible')}
              >
                <path
                  fill='#F8F3EF'
                  d='M24.61 54c-5.993 0-10.875-6.292-10.875-14.075V17.807C13.735 7.973 7.593 0 0 0v54'
                ></path>
              </svg>
            </div>
          </Link>
          <Link
            href='/attire'
            className={clsx(
              'flex h-10 flex-shrink flex-grow-0  basis-[220px] items-end justify-center lg_h-[60px] lg_flex-none lg_basis-[243px] ',
            )}
          >
            <div className='flex text-[0]'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='35'
                fill='none'
                viewBox='0 0 16 35'
                className={clsx(realActiveColumn !== 'attire' && 'invisible', 'flex-none lg_hidden')}
              >
                <path
                  fill='#F8F3EF'
                  d='M0 35c3.884 0 7.048-4.078 7.048-9.123V11.541C7.048 5.168 11.029 0 15.95 0v35'
                ></path>
              </svg>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='25'
                height='54'
                fill='none'
                viewBox='0 0 25 54'
                className={clsx('hidden flex-none lg_block', realActiveColumn !== 'attire' && 'invisible')}
                style={{ transform: 'rotateY(180deg)' }}
              >
                <path
                  fill='#F8F3EF'
                  d='M24.61 54c-5.993 0-10.875-6.292-10.875-14.075V17.807C13.735 7.973 7.593 0 0 0v54'
                ></path>
              </svg>

              <div
                className={clsx(
                  'relative flex flex-1 items-center justify-center px-[9px] lg_px-[14px]',
                  realActiveColumn === 'attire' &&
                    ' bg-f8f3ef after_absolute after_bottom-0 after_left-0 after_h-[1px] after_w-full after_bg-BE9983 after_bg-clip-content after_px-[9px] after_content-[""] lg_after_px-[14px]',
                )}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  xmlSpace='preserve'
                  x='0'
                  y='0'
                  version='1.1'
                  viewBox='0 0 204 30'
                  className={clsx('h-auto w-full lg_w-[204px]', realActiveColumn === 'attire' && 'bg-f8f3ef')}
                >
                  <path
                    d='M18.36 21.43c-.13-.06-.24-.14-.32-.24s-.18-.25-.3-.45-.24-.45-.37-.76l-.66-1.69c-.44-1.12-1.06-2.66-1.85-4.63-.79-1.96-1.54-3.79-2.24-5.48h-.22l-3.23 8.8c-.33.88-.62 1.6-.88 2.19-.26.58-.48 1.03-.65 1.35s-.34.55-.52.7-.32.25-.42.27c-.1.03-.25.04-.43.05v.25h4.32v-.25h-.14c-1.15-.01-1.73-.51-1.73-1.51 0-.08 0-.15.01-.24.03-.44.21-1.15.54-2.12h5.02l.67 1.68c.24.59.36 1.06.36 1.42 0 .51-.25.77-.77.77v.25h4.41v-.25c-.15-.01-.26-.02-.32-.03-.06.01-.15-.02-.28-.08m-9.04-3.99 2.34-6.42 2.53 6.42zM33.54 8.44c.35 0 .64.05.88.14s.43.21.56.34.22.32.26.57.07.47.07.66-.04.46-.11.79-.14.59-.19.78c-.06.19-.14.46-.25.81-.06.21-.11.36-.14.46l-2.18 6.22-3.17-9.12c-.03-.08-.04-.13-.04-.14s-.02-.09-.04-.24c-.03-.14-.03-.26-.02-.34s.04-.19.08-.33.11-.24.19-.32c.09-.08.22-.14.39-.21q.255-.09.6-.09v-.23h-5.21v.25c.2 0 .36.01.5.04s.29.09.46.19.33.25.48.46.29.48.42.82l1 2.88-2.13 6.32-3.33-9.05c-.02-.06-.03-.1-.04-.14-.01-.03-.02-.12-.04-.25s-.02-.24-.01-.33.04-.2.08-.33.11-.23.2-.32q.135-.12.39-.21c.255-.09.37-.09.6-.09v-.24h-5.22v.25c.2 0 .36.01.5.04s.29.09.46.19.33.25.48.46.29.48.41.82L25 21.81h.28l2.92-8.61 3.21 8.61h.27l3.17-8.75c.32-.85.6-1.54.85-2.08s.47-.98.68-1.33.42-.61.64-.78.43-.28.64-.34.46-.08.77-.08v-.26h-4.91v.25zM54.88 16.22c-.17-.24-.37-.45-.62-.63-.24-.18-.48-.32-.71-.41s-.48-.17-.77-.22c-.28-.05-.52-.09-.71-.11q-.285-.03-.6-.03c.12 0 .28-.02.48-.05.2-.04.46-.12.78-.26s.62-.32.88-.54.49-.55.69-.98q.3-.645.3-1.5c0-.35-.03-.66-.08-.94-.06-.28-.13-.53-.22-.75-.09-.21-.22-.4-.38-.57s-.32-.31-.48-.42-.36-.21-.61-.29-.47-.14-.68-.18-.46-.07-.77-.1-.58-.04-.82-.05-.53-.01-.87-.01h-3.83v.18c.75 0 1.12.7 1.12 2.11v9.01c0 1.44-.37 2.15-1.12 2.15v.17h3.81c.57 0 1.02 0 1.37-.01.34-.01.74-.04 1.2-.08.46-.05.82-.12 1.09-.21s.56-.22.86-.39.53-.37.69-.61.29-.54.4-.91.16-.78.16-1.25c0-.45-.05-.85-.16-1.22a2.6 2.6 0 0 0-.4-.9m-5.89-7.78h.85c.29 0 .54.01.75.04s.45.1.73.21c.27.12.5.28.67.48q.27.3.45.9c.12.39.18.86.18 1.4 0 .47-.03.89-.1 1.26s-.16.67-.28.91-.26.43-.41.59-.32.28-.52.35c-.2.08-.39.13-.57.16s-.38.04-.6.04h-1.15zm4.26 11.49c-.25.79-.76 1.28-1.52 1.48-.36.09-.99.14-1.89.14h-.85v-6.52h1.65c.14 0 .3.02.5.04q.3.03.51.09c.21.06.29.1.47.18.17.08.32.17.44.28s.24.24.36.41c.12.16.22.35.29.56s.13.45.18.74c.05.28.07.59.07.94 0 .67-.07 1.22-.21 1.66M67.56 19.86c-.11-.41-.22-.78-.31-1.13a9 9 0 0 0-.64-1.67 2.7 2.7 0 0 0-.35-.56c-.13-.16-.24-.29-.35-.41s-.23-.21-.37-.29l-.36-.18c-.1-.05-.22-.08-.37-.11s-.27-.04-.37-.05-.22-.02-.37-.03c.53-.04 1-.12 1.39-.24.4-.13.76-.32 1.08-.58s.57-.62.74-1.07.25-1 .25-1.65c0-.46-.04-.87-.13-1.22-.08-.37-.2-.67-.35-.93-.16-.26-.36-.48-.63-.67-.26-.19-.55-.34-.85-.45s-.67-.2-1.09-.27c-.43-.07-.86-.11-1.29-.13s-.94-.03-1.53-.03h-3.65v.25c.43 0 .74.1.92.3s.26.59.26 1.17v9.63q0 1.17-.27 1.59c-.27.42-.48.42-.91.42v.25h4.32v-.25c-.42 0-.72-.13-.92-.4-.2-.26-.3-.72-.3-1.36v-4.23h.88c.13 0 .25 0 .36.01.1.01.27.03.49.06s.42.09.6.19c.18.09.37.21.57.35s.38.34.53.59.27.54.36.86c.08.3.18.71.31 1.21.2.77.3 1.19.31 1.24s.07.21.17.48q.15.39.21.54c.06.15.13.21.26.34.14.13.24.21.31.23s.22.06.43.09.4.06.54.06h2.2v-.25c-1.14-.09-1.84-.66-2.08-1.7m-3.79-4.69c-.38.09-1.07.14-2.05.14h-.6V8.44h.74c.38 0 .66 0 .84.01.19.01.44.03.75.07.32.04.55.1.71.19s.34.21.55.37.36.37.46.61.18.55.26.92.11.79.11 1.27c0 .83-.1 1.49-.29 1.98-.26.69-.76 1.13-1.48 1.31M71.64 8.44c.45 0 .79.11 1.02.34s.34.62.34 1.19v9.77c0 1.21-.45 1.81-1.36 1.81v.25h4.71v-.25c-.9 0-1.35-.6-1.35-1.81V9.97c0-.57.11-.96.34-1.19s.56-.34 1.01-.34v-.25h-4.71zM89.9 11.2c-.26-.47-.58-.88-.97-1.24-.38-.36-.79-.65-1.22-.87s-.91-.4-1.44-.54-1.05-.23-1.56-.29c-.51-.05-1.06-.08-1.64-.08h-4.16v.25c.42 0 .74.18.97.55s.34.86.34 1.48v9.03c0 .65-.12 1.16-.34 1.52-.23.36-.55.53-.97.53v.25h4.16c.6 0 1.15-.02 1.64-.07s1.02-.13 1.56-.25 1.02-.29 1.45-.5c.42-.21.83-.49 1.22-.84s.71-.76.97-1.22.46-1.02.61-1.69.23-1.4.23-2.21c0-.79-.08-1.51-.23-2.16-.15-.62-.35-1.18-.62-1.65m-1.66 6.22c-.13.7-.29 1.27-.49 1.72s-.45.84-.78 1.16-.63.56-.9.72-.62.28-1.03.37c-.42.09-.75.14-1 .15s-.57.02-.97.02h-.85V8.44h.85c.37 0 .69.01.97.03q.42.03 1.02.18c.6.15.74.24 1.03.41s.59.42.9.75.57.72.77 1.18.36 1.03.49 1.71.19 1.47.19 2.33c-.01.9-.07 1.69-.2 2.39M104.04 21.43c-.13-.06-.24-.14-.32-.24s-.18-.25-.3-.45-.24-.45-.37-.76l-.66-1.69c-.44-1.12-1.06-2.66-1.85-4.63-.8-1.96-1.54-3.78-2.24-5.47h-.22l-3.23 8.8c-.33.88-.62 1.6-.88 2.19-.26.58-.48 1.03-.65 1.35s-.34.55-.52.7-.32.25-.42.27c-.1.03-.25.04-.43.05v.25h4.32v-.25h-.14c-1.15-.01-1.73-.51-1.73-1.51 0-.08 0-.15.01-.24.03-.44.21-1.15.54-2.12h5.02l.67 1.68c.24.59.36 1.06.36 1.42 0 .51-.25.77-.77.77v.25h4.41v-.25c-.15-.01-.26-.02-.32-.03-.06 0-.15-.03-.28-.09M95 17.44l2.34-6.42 2.53 6.42zM115.99 18.68c-.09.33-.2.61-.34.86-.13.25-.29.47-.46.67-.18.2-.37.37-.57.5s-.42.25-.64.36c-.23.11-.46.19-.7.24l-.72.15c-.24.05-.49.07-.72.07-.24 0-.47.01-.7.02-.11.01-.23.01-.34.01s-.22 0-.32-.01h-.7V10.16c0-.65.13-1.1.38-1.35.26-.25.62-.37 1.09-.37v-.25h-4.65v.25c.43 0 .75.13.95.38s.3.67.3 1.25v9.44c0 .69-.08 1.21-.24 1.54s-.45.51-.86.51v.25h9.66v-4.19h-.25c-.01.38-.08.74-.17 1.06M138.88 8.91l-5.15 12.9h1.51l1.55-4.02h5.57l1.55 4.02h1.49l-5.13-12.9zm-1.58 7.54 2.27-5.89 2.27 5.89zM145.78 10.22h4.57v11.59h1.39V10.22h4.55V8.91h-10.51zM157.83 10.22h4.57v11.59h1.39V10.22h4.55V8.91h-10.51zM171.14 8.91h1.39v12.9h-1.39zM183.95 16.03c.55-.34.99-.79 1.3-1.37s.46-1.24.46-1.99c0-1.24-.32-2.17-.96-2.8s-1.59-.95-2.85-.95h-5.3v12.9h1.39v-5.2h3.29l3.37 5.2h1.64l-3.51-5.36c.43-.08.82-.22 1.17-.43m-5.96-5.81h3.75c.99 0 1.67.23 2.03.69s.54 1.07.54 1.85q0 1.245-.72 1.89c-.72.645-1.26.65-2.33.65h-3.27zM197.73 10.22V8.91h-8.15v12.9h8.15V20.5h-6.76v-4.47h5.94v-1.36h-5.94v-4.45zM125.02 9.56h.31v11.05h-.31z'
                    className='fill-[#5e5047]'
                  ></path>
                </svg>
              </div>

              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='35'
                fill='none'
                viewBox='0 0 16 35'
                className={clsx(realActiveColumn !== 'attire' && 'invisible', 'flex-none lg_hidden')}
                style={{ transform: 'rotateY(180deg)' }}
              >
                <path
                  fill='#F8F3EF'
                  d='M0 35c3.884 0 7.048-4.078 7.048-9.123V11.541C7.048 5.168 11.029 0 15.95 0v35'
                ></path>
              </svg>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='25'
                height='54'
                fill='none'
                viewBox='0 0 25 54'
                className={clsx('hidden flex-none lg_block', realActiveColumn !== 'attire' && 'invisible')}
              >
                <path
                  fill='#F8F3EF'
                  d='M24.61 54c-5.993 0-10.875-6.292-10.875-14.075V17.807C13.735 7.973 7.593 0 0 0v54'
                ></path>
              </svg>
            </div>
          </Link>
        </div>

        {isMobile ? null : (
          <div className='hidden lg_flex lg_items-center lg_gap-x-10'>
            <Link
              href='/swatches'
              className='group flex items-center gap-x-1.5'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='23'
                fill='none'
                viewBox='0 0 19 23'
              >
                <g
                  className='fill-#403732 lg_group-hover_fill-80583F'
                  clipPath='url(#clip0_12101_8630)'
                >
                  <path d='M16.328 22.032H1.878A1.879 1.879 0 010 20.156V5.867h18.207v14.29a1.879 1.879 0 01-1.879 1.875zM1.058 6.919v13.234c0 .454.37.82.82.82h14.447c.454 0 .821-.37.821-.82V6.92H1.057z' />
                  <path d='M18.207 6.919H0V1.907C0 .872.842.031 1.878.031h14.447c1.036 0 1.878.841 1.878 1.876V6.92h.004zM1.057 5.863H17.15V1.907c0-.454-.37-.82-.822-.82H1.878a.824.824 0 00-.82.82v3.956z' />
                  <path d='M15.114 5.86H3.074v1.055h12.04V5.86z' />
                  <path d='M3.125 19.3h-.017c-.18-.003-.444-.07-.688-.366-.518-.626-.12-1.281.141-1.71.166-.271.339-.553.339-.81 0-.27-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.584 0-.275-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.58 0-.272-.088-.384-.247-.585-.2-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238l.159-.201.257-.004h12.296l.159.201c.2.253.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .27.088.384.247.584.2.254.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .271.088.384.247.584.2.254.476.599.476 1.24 0 .608-.29.992-.5 1.27-.205.27-.268.366-.22.58.085.387.255.651.417.908.236.373.599.94.063 1.588-.243.295-.507.362-.687.366h-.018c-.433 0-.73-.331-.99-.623-.116-.13-.363-.405-.462-.405-.098 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.634 0-.958-.419-1.17-.694-.187-.239-.236-.281-.335-.281-.098 0-.148.042-.334.282-.212.277-.536.693-1.17.693-.635 0-.96-.419-1.17-.694-.184-.239-.237-.281-.332-.281-.099 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.638 0-.958-.419-1.173-.694-.187-.239-.236-.281-.335-.281-.099 0-.342.274-.462.405-.257.288-.553.62-.987.62zm7.472-2.083c.638 0 .959.418 1.17.693.183.24.236.282.335.282.099 0 .148-.043.335-.282.211-.278.535-.693 1.17-.693.546 0 .93.4 1.212.714-.003-.007-.01-.014-.014-.021-.183-.285-.43-.68-.557-1.25-.151-.696.173-1.126.409-1.439.187-.25.286-.39.286-.633 0-.271-.089-.384-.247-.585-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.2.247-.31.247-.585 0-.27-.089-.383-.247-.584-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.201.247-.31.247-.585a.666.666 0 00-.1-.38H3.338a.664.664 0 00-.102.387c0 .271.088.384.246.585.201.253.476.598.476 1.238 0 .641-.275.986-.476 1.24-.158.2-.246.309-.246.583 0 .271.088.384.246.585.201.253.476.598.476 1.239 0 .64-.275.985-.476 1.238-.158.201-.246.31-.246.585 0 .274.088.383.246.584.201.253.476.598.476 1.239 0 .552-.275 1-.493 1.358-.025.042-.057.088-.085.141.282-.31.66-.693 1.195-.693.638 0 .958.418 1.173.693.187.24.237.281.335.281.099 0 .148-.042.335-.281.212-.278.536-.693 1.17-.693.635 0 .959.418 1.17.693.184.24.236.281.332.281.098 0 .148-.042.334-.281.215-.282.536-.7 1.174-.7zM15.508 2.25H8.48v1.056h7.028V2.25zM15.506 4.14h-3.514v1.056h3.514V4.141z' />
                </g>
                <defs>
                  <clipPath id='clip0_12101_8630'>
                    <path
                      fill='#fff'
                      d='M0 0H18.207V22H0z'
                      transform='translate(0 .031)'
                    />
                  </clipPath>
                </defs>
              </svg>
              <span className='text-sm leading-[19px] text-#403732 lg_group-hover_text-80583F'>
                Get 5 Free Swatches
              </span>
            </Link>
            <CountrySwitcher />
          </div>
        )}
      </div>
      <div
        className='mx-auto flex h-[66px] items-center justify-between gap-x-5 bg-f8f3ef px-[15px] lg_hidden'
        ref={mobileHeaderMenuRef}
      >
        {navTrigger}
        <button
          type='button'
          className='flex h-10 min-w-0 flex-1 items-center gap-x-2.5 rounded-[30px] border-none bg-white px-2.5 outline-0'
          onClick={() => {
            setIsSearchOpen(true);
          }}
        >
          <div className='min-w-0 flex-1 text-[15px] text-[#929292]'>
            {websiteEnv.isUnitedKingdom ? 'Search by colour or name' : 'Search by color or name'}
          </div>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='19'
            height='18'
            fill='none'
            viewBox='0 0 19 18'
            className='flex-none'
          >
            <path
              fill='#80583F'
              fillRule='evenodd'
              d='M9.173 15.506c2.023 0 3.843-.81 5.124-2.158 1.28-1.28 2.157-3.1 2.157-5.123s-.809-3.843-2.157-5.124C13.016 1.821 11.196.944 9.173.944S5.331 1.753 4.05 3.1C2.769 4.382 1.892 6.202 1.892 8.225s.81 3.842 2.158 5.123c1.28 1.349 3.1 2.158 5.123 2.158m6.135-1.82 3.64 3.64-.674.674-3.64-3.64c-1.483 1.28-3.37 2.09-5.46 2.09-2.293 0-4.315-.944-5.799-2.427S.948 10.449.948 8.225c0-2.292.944-4.315 2.427-5.798S6.948 0 9.173 0c2.292 0 4.315.944 5.798 2.427S17.398 6 17.398 8.225c0 2.09-.81 4.045-2.09 5.46'
              clipRule='evenodd'
            ></path>
          </svg>
        </button>
        <div className='flex flex-none items-center gap-x-[15px]'>
          <CountrySwitcher />
          <CartTrigger
            miniCartOpen={miniCartOpen}
            setMiniCartOpen={setMiniCartOpen}
            setShowNoItemDrawer={setShowNoItemDrawer}
          />
        </div>
      </div>
      <SearchBarLazyLoad
        isSearchOpen={isSearchOpen}
        setIsSearchOpen={setIsSearchOpen}
      />
    </div>
  );
};

export default HeaderToolbar;
